import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Button,
  Divider,
  Skeleton,
  IconButton,
} from "@mui/material";

import VerifiedIcon from "@mui/icons-material/Verified";
import StoreIcon from "@mui/icons-material/Store";
import EventIcon from "@mui/icons-material/Event";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";

import { formatDate } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";

/**
 * HomeTab
 * - KPI summary
 * - Latest device summary
 * - Raise claim CTA
 */
export default function HomeTab() {
  const {
    bootLoading,
    devices = [],
    onRaiseClaim,
    onViewPurchase,
  } = useOutletContext();

  /* ---------------- DERIVED DATA ---------------- */

  const devicesCount = devices.length;

  const claimsCount = useMemo(() => {
    return devices.reduce(
      (sum, d) => sum + Number(d.available_claim || 0),
      0
    );
  }, [devices]);

  const latestDevice = useMemo(() => {
    if (!devices.length) return null;

    return [...devices].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )[0];
  }, [devices]);

  const uniqueBrandCount = useMemo(() => {
    return new Set(
      devices
        .map((d) => d.brand_name)
        .filter(Boolean)
        .map((b) => b.toLowerCase())
    ).size;
  }, [devices]);

  /* ---------------- RENDER ---------------- */

  return (
    <Stack spacing={3}>
      {/* WARRANTY STATUS */}
      <Card
        sx={{
          borderRadius: 4,
          px: 2,
          py: 1.2,
          color: "#fff",
          fontWeight: 600,
          background: "linear-gradient(135deg, #1e6fd9, #2f80ed)",
          boxShadow: "0 6px 16px rgba(47, 128, 237, 0.35)",
          textTransform: "none",
          "&:hover": {
            background: "linear-gradient(135deg, #1b63c2, #2563eb)",
            boxShadow: "0 8px 20px rgba(37, 99, 235, 0.45)",
          },
        }}

      >
        <CardContent>
          <Typography variant="body2">Warranty Status</Typography>

          {bootLoading ? (
            <Skeleton variant="text" width={160} height={44} />
          ) : (
            <Typography variant="h4" fontWeight={800}>
              Active
            </Typography>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <VerifiedIcon fontSize="small" />
            <Typography variant="body2">
              {devicesCount
                ? "Your devices are protected"
                : "No active devices"}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* KPI CARDS */}
      <Stack direction="row" spacing={2}>
        <Card sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="caption">Devices</Typography>
            {bootLoading ? (
              <Skeleton width={60} height={36} />
            ) : (
              <Typography variant="h5" fontWeight={800}>
                {devicesCount}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="caption">Claims Balance</Typography>
            {bootLoading ? (
              <Skeleton width={60} height={36} />
            ) : (
              <Typography variant="h5" fontWeight={800}>
                {claimsCount}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* DEVICE SUMMARY */}
      <Card sx={{ borderRadius: 4 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#eef2ff" }}>
              <StoreIcon color="primary" />
            </Avatar>

            <Box flex={1}>
              <Typography fontWeight={900}>
                My Purchases
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {devicesCount} device
                {devicesCount !== 1 ? "s" : ""} •{" "}
                {uniqueBrandCount} brand
                {uniqueBrandCount !== 1 ? "s" : ""}
              </Typography>
            </Box>

            <IconButton onClick={onViewPurchase}>
              <ListAltIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {latestDevice ? (
            <Stack spacing={1}>
              <Typography fontWeight={900}>
                {latestDevice.product_name || "Device"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {latestDevice.brand_name} •{" "}
                {latestDevice.category_name}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <EventIcon fontSize="small" />
                <Typography variant="body2">
                  Expiry:{" "}
                  <b>{formatDate(latestDevice.expiry_date)}</b>
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                IMEI: {latestDevice.imei1 || "—"} •{" "}
                Serial: {latestDevice.serial || "—"}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No devices found.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* CTA */}
      <Button
        size="large"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => onRaiseClaim(null)}
        sx={{ borderRadius: 3, py: 1.2 }}
      >
        Raise a Claim
      </Button>
    </Stack>
  );
}



// // src/tabs/HomeTab.jsx
// import React, { useMemo, useState } from "react";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Stack,
//   Avatar,
//   Button,
//   Divider,
//   Skeleton,
//   IconButton,
// } from "@mui/material";

// import VerifiedIcon from "@mui/icons-material/Verified";
// import StoreIcon from "@mui/icons-material/Store";
// import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
// import EventIcon from "@mui/icons-material/Event";
// import ListAltIcon from "@mui/icons-material/ListAlt";
// import AddIcon from "@mui/icons-material/Add";
// import { formatDate } from "../../../utils/helpers";
// import { useOutletContext } from "react-router-dom";



// /**
//  * HomeTab
//  * - KPI summary
//  * - Latest purchase
//  * - Multi-store awareness
//  * - Raise claim CTA
//  */
// export default function HomeTab() {
//   const {
//     bootLoading,
//     devices,
//     onRaiseClaim,
//     onOpenPurchases,
//     onViewPurchase,
//   } = useOutletContext();
  
//   const [claimStep, setClaimStep] = useState(0);
//   const [openClaim, setOpenClaim] = useState(false);
//   const [claimDraft, setClaimDraft] = useState({
//     device_id: "",
//     issues: [],
//     notes: "",
//     photos: [],
//     service: "pickup",
//     pickup_address_id: "",
//     drop_pincode: "",
//     drop_partner_id: "",
//   });
//   /** 🔹 MOCK DATA (replace with API later) */
//   const purchases = [
//     {
//       id: "pur-1",
//       model_name: "Google Pixel 8",
//       purchase_date: "2025-12-20",
//       order_no: "ORD-77881",
//       serial_no: "SN-PXL8-88991234",
//       imei: "356789101112131",
//       warranty_expiry: "2026-12-20",
//       purchased_from: {
//         name: "Akash Mobile World",
//       },
//     },
//     {
//       id: "pur-2",
//       model_name: "Samsung Galaxy S24",
//       purchase_date: "2025-11-08",
//       order_no: "ORD-66420",
//       serial_no: "SN-S24-10012025",
//       imei: "357891011121314",
//       warranty_expiry: "2026-11-08",
//       purchased_from: {
//         name: "City Mobiles",
//       },
//     },
//   ];

//   const [addresses, setAddresses] = useState([
//     {
//       id: "local-1",
//       name: "Rohit Sharma",
//       mobile: "9876543210",
//       address1: "Flat 301, ABC Apartment",
//       address2: "Near City Mall",
//       city: "Pune",
//       state: "Maharashtra",
//       pincode: "411001",
//       lat: 18.5204,
//       lng: 73.8567,
//     },
//   ]);

//   const devicesCount = 2;
//   const claimsCount = 2;

//   /** 🔹 COMPUTED */
//   const purchasesSorted = useMemo(() => {
//     return [...purchases].sort(
//       (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
//     );
//   }, []);

//   const latestPurchase = purchasesSorted[0];
//   const uniqueShopCount = new Set(
//     purchases.map((p) => p.purchased_from?.name?.toLowerCase())
//   ).size;

//   const startClaim = (preselectedDeviceId) => {
//     const firstAddressId = addresses?.[0]?.id || "";
//     setClaimDraft({
//       device_id: preselectedDeviceId || "",
//       issues: [],
//       notes: "",
//       photos: [],
//       service: "pickup",
//       pickup_address_id: firstAddressId,
//       drop_pincode: "",
//       drop_partner_id: "",
//     });
//     setClaimStep(preselectedDeviceId ? 1 : 0);
//     setOpenClaim(true);
//   };

//   return (
//     <Stack spacing={3}>
//       {/* ================= WARRANTY STATUS ================= */}
//       <Card
//         sx={{
//           borderRadius: 4,
//           p: 1,
//           color: "#fff",
//           background: "linear-gradient(135deg,#4f46e5,#6366f1)",
//         }}
//       >
//         <CardContent>
//           <Typography variant="body2">Warranty Status</Typography>

//           {bootLoading ? (
//             <Skeleton variant="text" width={160} height={44} />
//           ) : (
//             <Typography variant="h4" fontWeight={800}>
//               Active
//             </Typography>
//           )}

//           <Stack direction="row" spacing={1} alignItems="center">
//             <VerifiedIcon fontSize="small" />
//             <Typography variant="body2">
//               All devices protected
//             </Typography>
//           </Stack>
//         </CardContent>
//       </Card>

//       {/* ================= KPI CARDS ================= */}
//       <Stack direction="row" spacing={2}>
//         <Card
//           sx={{
//             flex: 1,
//             borderRadius: 3,
//             cursor: "pointer",
//             "&:hover": { transform: "translateY(-1px)" },
//           }}
//         >
//           <CardContent>
//             <Typography variant="caption">Devices</Typography>
//             {bootLoading ? (
//               <Skeleton width={60} height={36} />
//             ) : (
//               <Typography variant="h5" fontWeight={800}>
//                 {devicesCount}
//               </Typography>
//             )}
//           </CardContent>
//         </Card>

//         <Card
//           sx={{
//             flex: 1,
//             borderRadius: 3,
//             cursor: "pointer",
//             "&:hover": { transform: "translateY(-1px)" },
//           }}
//         >
//           <CardContent>
//             <Typography variant="caption">Claims</Typography>
//             {bootLoading ? (
//               <Skeleton width={60} height={36} />
//             ) : (
//               <Typography variant="h5" fontWeight={800}>
//                 {claimsCount}
//               </Typography>
//             )}
//           </CardContent>
//         </Card>
//       </Stack>

//       {/* ================= PURCHASE SUMMARY ================= */}
//       <Card
//         sx={{
//           borderRadius: 4,
//           cursor: "pointer",
//           "&:hover": { transform: "translateY(-1px)" },
//         }}
//       >
//         <CardContent>
//           <Stack direction="row" spacing={1.5} alignItems="center">
//             <Avatar sx={{ bgcolor: "#eef2ff" }}>
//               <StoreIcon color="primary"  />
//             </Avatar>

//             <Box flex={1}>
//               <Typography fontWeight={900}>
//                 Where Purchased
//               </Typography>
//               <Typography variant="body2" color="text.secondary">
//                 {purchases.length} purchase
//                 {purchases.length !== 1 ? "s" : ""} •{" "}
//                 {uniqueShopCount} shop
//                 {uniqueShopCount !== 1 ? "s" : ""}
//               </Typography>
//             </Box>

//             <IconButton>
//               <ListAltIcon onClick={onViewPurchase} />
//             </IconButton>
//           </Stack>

//           <Divider sx={{ my: 1.5 }} />

//           {latestPurchase ? (
//             <Stack spacing={1}>
//               <Typography fontWeight={900}>
//                 {latestPurchase.model_name}
//               </Typography>

//               <Typography variant="body2" color="text.secondary">
//                 {latestPurchase.purchased_from?.name} •{" "}
//                 {formatDate(latestPurchase.purchase_date)}
//               </Typography>

//               <Stack direction="row" spacing={1} alignItems="center">
//                 <ReceiptLongIcon fontSize="small" />
//                 <Typography variant="body2">
//                   Order No:{" "}
//                   <b>{latestPurchase.order_no}</b>
//                 </Typography>
//               </Stack>

//               <Typography variant="caption" color="text.secondary">
//                 S/N: {latestPurchase.serial_no}
//                 {latestPurchase.imei &&
//                   ` • IMEI: ${latestPurchase.imei}`}
//                 {latestPurchase.warranty_expiry &&
//                   ` • Expiry: ${formatDate(
//                     latestPurchase.warranty_expiry
//                   )}`}
//               </Typography>
//             </Stack>
//           ) : (
//             <Typography variant="body2" color="text.secondary">
//               No purchase found.
//             </Typography>
//           )}
//         </CardContent>
//       </Card>

//       {/* ================= CTA ================= */}
//       <Button
//         size="large"
//         variant="contained"
//         startIcon={<AddIcon />}
//       onClick={() => onRaiseClaim(null)}
//         sx={{ borderRadius: 3, py: 1.2 }}
//       >
//         Raise a Claim
//       </Button>

//     </Stack>
//   );
// }



// import React from "react";

// import { ShieldCheck, Store, List } from "lucide-react";
// import Card, { CardBody } from "../ui/Card";

// export default function HomeTab({
//   deviceCount,
//   claimCount,
//   purchases,
//   uniqueShopCount,
//   latestPurchase,
//   onGoDevices,
//   onGoClaims,
//   onOpenPurchases,
//   onStartClaim,
// }) {
//   return (
//     <div className="space-y-4">
//       <Card className="bg-gradient-to-br from-indigo-600 to-indigo-400 border-0 text-white">
//         <CardBody className="p-5">
//           <div className="text-sm opacity-90">Warranty Status</div>
//           <div className="text-3xl font-extrabold mt-1">Active</div>
//           <div className="flex items-center gap-2 mt-2 text-sm">
//             <ShieldCheck size={16} />
//             <span className="opacity-95">All devices protected</span>
//           </div>
//         </CardBody>
//       </Card>

//       <div className="grid grid-cols-2 gap-3">
//         <Card onClick={onGoDevices}>
//           <CardBody>
//             <div className="text-xs text-slate-500 font-semibold">Devices</div>
//             <div className="text-2xl font-extrabold text-slate-900 mt-1">{deviceCount}</div>
//           </CardBody>
//         </Card>
//         <Card onClick={onGoClaims}>
//           <CardBody>
//             <div className="text-xs text-slate-500 font-semibold">Claims</div>
//             <div className="text-2xl font-extrabold text-slate-900 mt-1">{claimCount}</div>
//           </CardBody>
//         </Card>
//       </div>

//       <Card onClick={() => onOpenPurchases?.(latestPurchase?.id)}>
//         <CardBody>
//           <div className="flex items-center gap-3">
//             <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center">
//               <Store className="text-indigo-600" size={18} />
//             </div>
//             <div className="flex-1">
//               <div className="font-extrabold text-slate-900">Where Purchased</div>
//               <div className="text-sm text-slate-500">
//                 {purchases.length} purchase{purchases.length === 1 ? "" : "s"} • {uniqueShopCount} shop
//                 {uniqueShopCount === 1 ? "" : "s"}
//               </div>
//             </div>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 onOpenPurchases?.(latestPurchase?.id);
//               }}
//               className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50"
//             >
//               <List size={18} className="text-slate-700" />
//             </button>
//           </div>

//           <div className="h-px bg-slate-100 my-3" />

//           {latestPurchase ? (
//             <div className="space-y-1">
//               <div className="font-extrabold text-slate-900">{latestPurchase.model_name}</div>
//               <div className="text-sm text-slate-500">
//                 {latestPurchase.purchased_from?.name} • {latestPurchase.purchase_date}
//               </div>
//               <div className="text-xs text-slate-500">
//                 Order No: <b className="text-slate-700">{latestPurchase.order_no}</b>
//               </div>
//               <div className="text-xs text-slate-500">
//                 S/N: {latestPurchase.serial_no}
//                 {latestPurchase.imei ? ` • IMEI: ${latestPurchase.imei}` : ""}
//                 {latestPurchase.warranty_expiry ? ` • Expiry: ${latestPurchase.warranty_expiry}` : ""}
//               </div>
//             </div>
//           ) : (
//             <div className="text-sm text-slate-500">No purchase found.</div>
//           )}
//         </CardBody>
//       </Card>

//       <button
//         onClick={onStartClaim}
//         className="w-full rounded-2xl bg-indigo-600 text-white font-extrabold py-3.5 shadow hover:bg-indigo-700 transition"
//       >
//         Raise a Claim
//       </button>
//     </div>
//   );
// }


